<style scoped lang="less">
  .user-list-wrapper {
    width: 400px;
    & > .placeholder{
      font-size: 14px;
      font-weight: 400;
      color: rgba(0,0,0,.25);
      line-height: 20px;
      padding: 14px 24px;
      border-bottom: 1px solid #EBEBEB;
    }
    & > .user-list-wrap {
      max-height: 200px;
      .fetch-loading {
        text-align: center;
        line-height: 100px;
      }
      .empty-tips {
        color: #999;
        text-align: center;
        line-height: 20px;
        padding: 40px 0;
      }
      .user-item {
        padding: 8px 24px;
        transition: all .15s;
        cursor: pointer;
        position: relative;
        .selected-icon {
          width: 16px;
          height: 16px;
          position: absolute;
          top: 50%;
          right: 16px;
          transform: translateY(-50%);
          user-select: none;
          pointer-events: none;
          display: none;
        }
        &.disabled {
          cursor: not-allowed;
          opacity: .6;
        &:hover {
            background: transparent !important;
          }
        }
        &.selected {
          .selected-icon {
            display: block;
          }
        }
        &.actived,&:hover {
          background: #f3f4f5;
        }
      }
    }
    & > .footer {
      text-align: right;
      border-top: 1px solid #EBEBEB;
      padding: 8px 24px;
    }
  }
</style>
<template>
  <div class="user-list-wrapper" :id="id" :style="{ width: width + 'px' }">
    <div class="placeholder">通过姓名搜索</div>
    <div class="user-list-wrap scroll-mini" >
      <div style="position: relative" ref="userListOptionsWrapperRef">
        <template v-if="fetchLoading">
          <div class="fetch-loading">
            <Spin>
              <template #indicator>
                <LoadingOutlined />
              </template>
            </Spin>
          </div>
        </template>
        <template v-if="!fetchLoading && searchValue && !userOptions.length">
          <div class="empty-tips">没有搜索到成员</div>
        </template>
        <!-- 这里增加正在loading时会显示结果的问题-->
        <template v-else-if="!fetchLoading">
          <template v-for="(user, userIndex) in userOptions" :key="user.userId">
            <Tooltip
              :getPopupContainer="() => body"
              :visible="innerDisabledTipsByUserId[user.userId] ? undefined : false"
              :autoAdjustOverflow="false"
              :destroyTooltipOnHide="true"
              :mouseEnterDelay="0.5"
              :mouseLeaveDelay="0"
              placement="left"
              overlayClassName="custom-tooltip"
            >
              <template #title>
                {{ innerDisabledTipsByUserId[user.userId] }}
              </template>
              <div
                class="user-item"
                :id="getUserItemId(user.userId)"
                :class="{
                  selected: selecteUsers.includes(user.userId),
                  actived: activedUserIndex === userIndex,
                  disabled: innerDisabledUsers.includes(user.userId)
                }"
                @click="() => !innerDisabledUsers.includes(user.userId) && onClickUser(user)"
              >
                <userView :userInfo="user" />
                <img src="./assets/selected.png"  class="selected-icon" />
              </div>
            </Tooltip>
          </template>
        </template>
      </div>
    </div>
    <div class="footer" v-if="userOptions.length && multiple">
      <Button type="primary" @click="emitSelectedChange(selecteUsers)">确定</Button>
    </div>
  </div>
</template>
<script setup>
import { uniq } from 'lodash-es'
import { watch, toRefs, ref, computed, onMounted, onUnmounted, watchEffect } from 'vue'
import { Button, Spin, Tooltip } from 'ant-design-vue'
import { LoadingOutlined } from '@ant-design/icons-vue'
import userView from './user.vue'
import { debounce } from 'lodash-es'

// 根节点
const userListOptionsWrapperRef = ref()
const body = document.body

// emits | props
const emits = defineEmits([ 'select' ])
const props = defineProps({
  id: {
    type: String,
    default: 'user-options-container-' + Math.random().toString().substring(2)
  },
  width: {
    type: Number,
    default: 400
  },
  searchValue: {
    type: String,
    default: ''
  },
  isFocus: {  // 是否是激活状态
    type: Boolean,
    default: false
  },
  // 是否支持多选
  multiple: {
    type: Boolean,
    default: false
  },
  // 当前置灰的用户
  disabledUsers: {
    type: [Array, Function],
    default: () => []
  },
  // 默认选中的用户
  defaultSelectedUsers: {
    type: Array,
    default: () => []
  },
  // 请求用户的接口
  searchUserApi: {
    type: Function
  }
})
const { width, multiple, searchValue, disabledUsers: getDisabledUsers, defaultSelectedUsers, isFocus } = toRefs(props)

const getUserItemId = (userId) => props.id + '-user-item-' + userId
const userOptions = ref([]) // 待选的用户
const selecteUsers = ref([])  // 当前选中的用户
const activedUserIndex = ref(-1)  // 当前通过键盘激活的用户Index

// 清除本次选中，但是不要清除默认选中的部分
const clearSelecteUsers = () => {
  selecteUsers.value = uniq([ ...defaultSelectedUsers.value ])
}

// 将默认选中的用户添加到已选择列表中
watch(defaultSelectedUsers, () => {
  clearSelecteUsers()
}, { deep: true, immediate: true })

// 内部判断好的当前被disable的用户
const innerDisabledUsers = ref([])
const innerDisabledTipsByUserId = ref({})
watch([ getDisabledUsers, userOptions ], () => {
  innerDisabledTipsByUserId.value = {}  // 清空disabled tips
  if (typeof getDisabledUsers.value === 'function') {
    // 这里应该返回一个userIds数组
    const users = getDisabledUsers.value(userOptions.value)
    innerDisabledUsers.value = users.map(item => {
      if (Array.isArray(item)) {
        innerDisabledTipsByUserId.value[item[0]] = item[1]
        return item[0]
      }
      else return item
    })
  }
  else if (Array.isArray(getDisabledUsers.value)) {
    innerDisabledUsers.value = [ ...getDisabledUsers.value ]
  }
})
// 是否当前的筛选项已经全部被禁用
const isAllUsersDisabled = computed(() => {
  return (userOptions.value.filter(user => innerDisabledUsers.value.includes(user.userId))).length === userOptions.value.length
})

/**
 * 点击某一个用户，判断是否选中
 */
const onClickUser = (user) => {
  const userId = user.userId
  if (multiple.value && selecteUsers.value.includes(userId)) {
    selecteUsers.value = selecteUsers.value.filter(id => id !== userId)
  } else if (multiple.value) {
    selecteUsers.value.push(userId)
  } else if (!multiple.value) {
    emitSelectedChange([ userId ])
  }
}

/**
 * 通知父级
 */
const emitSelectedChange = (userIds) => {
  const selectedUserMap = {}
  const allSelectUserIds = uniq(userIds)
  userOptions.value.forEach(user => {
    if (allSelectUserIds.includes(user.userId)) {
      selectedUserMap[user.userId] = user
    }
  })
  clearSelecteUsers()
  emits('select', allSelectUserIds, userIds, selectedUserMap)
}

// 请求用户列表
const fetchingMap = ref({})
const fetchLoading = computed(() => Object.keys(fetchingMap.value).length !== 0)
const fetchUsers = debounce(async (userName, onSuccess) => {
  if (!userName) return
  const fetchkey = userName + Date.now() + Math.random()
  fetchingMap.value[fetchkey] = true
  try {
    const users = await props.searchUserApi({ userName })
    if (searchValue.value === userName && fetchingMap.value[fetchkey]) {
      const data = users.map(item => ({ ...item, deptNames: item.deptNames || [], label: item.name, value: item.userId }))
      onSuccess && onSuccess(data)
    }
  } catch (e) {
    console.error(e, '错误')
  } finally {
    delete fetchingMap.value[fetchkey]
  }
}, 300)


/**
 * 搜索值变化，重新请求用户
 */
 watch(searchValue, (newValue) => {
  if (newValue) {
    fetchingMap.value['watch'] = true   // 用于立即触发loading显示
    fetchUsers(newValue, (users) => {
      userOptions.value = users
      delete fetchingMap.value['watch']
    })
  } else {
    userOptions.value = []
    fetchingMap.value = {}
    clearSelecteUsers()
  }
  // 重置键盘选中态
  activedUserIndex.value = -1
}, { immediate: true })


/** 循环选中当前用户列表，跳过disabled用户 */
const loopNomalUsers = (index, added = 1) => {
  let newIndex = index + added
  if (newIndex < 0) { newIndex = userOptions.value.length - 1 }
  else if (newIndex >= userOptions.value.length) { newIndex = 0 }
  // 如果当前用户被禁用了，则多跳一个
  if (innerDisabledUsers.value.includes(userOptions.value[newIndex].userId)) {
    newIndex = loopNomalUsers(newIndex, added)
  }
  return newIndex
}

/**
 * 监听键盘事件
 */
const watchBindPageDown = () => {
  if (!isAllUsersDisabled.value && isFocus.value) {
    activedUserIndex.value = loopNomalUsers(activedUserIndex.value , 1)
  }
}

const watchBindPageUp = () => {
  if (!isAllUsersDisabled.value && isFocus.value) {
    activedUserIndex.value = loopNomalUsers(activedUserIndex.value , -1)
  }
}
const watchBindEnterkey = () => {
  if (activedUserIndex.value === -1)  { return }
  onClickUser(userOptions.value[activedUserIndex.value])
}
const bindKeyboardEvent = (event) => {
  if (event.key === 'ArrowUp') { watchBindPageUp() }
  else if(event.key === 'ArrowDown') {  watchBindPageDown() }
  else if(event.key === 'Enter'){  watchBindEnterkey() }
}

// 监听 并 滚动子元素到父元素的可视区域内
function scrollParentToChild(child) {
  const parent = document.querySelector('#' + props.id + ' .scroll-mini')
  parent.scrollTop = child.offsetTop + child.clientHeight - parent.clientHeight
}
watch(activedUserIndex, () => {
  const selectOptionUser = userOptions.value[activedUserIndex.value]
  if (selectOptionUser?.userId) {
    const userEl = document.querySelector('#' + getUserItemId(selectOptionUser?.userId))
    if (userEl) scrollParentToChild(userEl) 
  }
})

// 改变监听键盘的方式
watchEffect(() => {
  if (isFocus.value) {
    window.addEventListener('keyup', bindKeyboardEvent)
  } else {
    window.removeEventListener('keyup', bindKeyboardEvent)
  }
})
onUnmounted(() => window.removeEventListener('keyup', bindKeyboardEvent))
</script>
